import React from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import { formatYearMonthDate, serializeOptions } from 'utils'
import { Input, Select } from 'lp-components'
import {
  NestedAttributesFieldArray,
  ReadOnlyCellInput,
  DateInput,
} from 'components'

const propTypes = {
  flashErrorMessage: PropTypes.func.isRequired,
  experiences: PropTypes.array,
  change: PropTypes.func.isRequired,
}

const defaultProps = {}

const EMPLOYMENT_TYPE_OPTIONS = [
  { key: 'Full-Time', value: 'full_time' },
  { key: 'Part-Time', value: 'part_time' },
  { key: 'Contract', value: 'contract' },
]

function EndDateDisplay({ input }) {
  return (
    <span>{input.value ? formatYearMonthDate(input.value) : 'Present'}</span>
  )
}

function ExperienceRow({
  fields,
  index,
  change,
  experiences,
  flashErrorMessage,
}) {
  const field = fields.name ? `${fields.name}[${index}]` : `${index}`
  const experience = fields.get(index)
  const isEditing = experience?.isEditing
  // Explicit boolean stored on row state; falls back to !endDate for rows
  // freshly loaded from the API that don't have the field yet.
  const isCurrentPosition =
    experience?.isCurrentPosition !== undefined
      ? experience.isCurrentPosition
      : !experience?.endDate

  const setIsEditing = (isEditing) => {
    const canSave =
      experience.title &&
      experience.ftPtCo &&
      experience.organizationName &&
      experience.fullLocation &&
      experience.startDate
    if (!isEditing) {
      if (!canSave) {
        flashErrorMessage('Please fill in all fields before saving.')
        return
      }
    }
    const updatedExperience = { ...experience, isEditing }

    const updatedExperiences = [...experiences]
    updatedExperiences[index] = updatedExperience
    change('experiences', updatedExperiences)
  }

  return (
    <div key={experience.id || index} className="form-table-row">
      <div className="form-table-cell">
        <Field
          name={field + '.title'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="Enter title"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.ftPtCo'}
          label={false}
          component={isEditing ? Select : ReadOnlyCellInput}
          placeholder="Select employment type"
          options={serializeOptions(EMPLOYMENT_TYPE_OPTIONS, {
            key: 'key',
            value: 'value',
          })}
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.organizationName'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="Enter organization name"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.fullLocation'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="City, State, Country"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.startDate'}
          label={false}
          component={isEditing ? DateInput : ReadOnlyCellInput}
          placeholderText="MM/DD/YYYY"
          popperPlacement="top"
          className="label-block"
          format={formatYearMonthDate}
          parse={formatYearMonthDate}
        />
      </div>
      <div className="form-table-cell">
        {isEditing ? (
          <>
            {!isCurrentPosition && (
              <Field
                name={field + '.endDate'}
                label={false}
                component={DateInput}
                placeholderText="MM/DD/YYYY"
                popperPlacement="top"
                className="label-block"
                format={formatYearMonthDate}
                parse={formatYearMonthDate}
                normalize={(value) => (value == null ? '' : value)}
              />
            )}
            <label className="current-position-label">
              <input
                type="checkbox"
                checked={isCurrentPosition}
                onChange={(e) => {
                  const checked = e.target.checked
                  const updatedExperiences = [...experiences]
                  updatedExperiences[index] = {
                    ...experience,
                    isCurrentPosition: checked,
                    endDate: checked ? '' : experience.endDate || '',
                  }
                  change('experiences', updatedExperiences)
                }}
              />{' '}
              Current Position
            </label>
          </>
        ) : (
          <Field
            name={field + '.endDate'}
            label={false}
            component={EndDateDisplay}
          />
        )}
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.description'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="Describe your experience"
        />
      </div>
      <div className="form-table-cell">
        <button
          type="button"
          className="button-primary"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Save' : 'Edit'}
        </button>
      </div>
      <div className="form-table-cell">
        <button
          type="button"
          className="remove-row"
          aria-label={`Remove ${experience.title || 'experience'} entry`}
          onClick={() => fields.destroy(index)}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  )
}

function ExperienceFields({ fields, change, experiences, flashErrorMessage }) {
  return (
    <>
      {fields.map((_, i) => {
        return (
          <ExperienceRow
            key={`experience-${i}`}
            index={i}
            fields={fields}
            change={change}
            experiences={experiences}
            flashErrorMessage={flashErrorMessage}
          />
        )
      })}
    </>
  )
}

function ExperienceSection({ change, experiences, flashErrorMessage }) {
  const addExperienceEntry = () => {
    const newExperience = {
      organizationName: '',
      isEditing: true,
    }
    change('experiences', [...experiences, newExperience])
  }
  return (
    <section>
      <div>
        <div className="form-table">
          <div className="form-table-heading">
            <div className="form-table-row">
              <div className="form-table-head">Title</div>
              <div className="form-table-head">Employment Type</div>
              <div className="form-table-head">Company or Org</div>
              <div className="form-table-head">Location</div>
              <div className="form-table-head">Start Date</div>
              <div className="form-table-head">End Date / Current</div>
              <div className="form-table-head">Description</div>
              <div className="form-table-head" style={{ width: 70 }}></div>
              <div className="form-table-head" style={{ width: 20 }}></div>
            </div>
          </div>
          <div className="form-table-body">
            <NestedAttributesFieldArray
              name="experiences"
              component={ExperienceFields}
              change={change}
              experiences={experiences}
              flashErrorMessage={flashErrorMessage}
            />
          </div>
        </div>
      </div>
      <button type="button" className="link-black" onClick={addExperienceEntry}>
        + Add New Experience
      </button>
    </section>
  )
}

ExperienceSection.propTypes = propTypes
ExperienceSection.defaultProps = defaultProps

export default ExperienceSection
